import api from "@/app/api";
import type { CompanyUserUpdate } from "@/app/api/types/company";
import type {
  GroupCreate,
  GroupMemberRoleValue,
  GroupOption,
  GroupResponse,
  GroupUserCreate,
  GroupUserCreateResponse,
} from "@/app/api/types/group";
import { v2UpdateGroup } from "@/lib/v2-api/groups";
import { invalidateMemberGraph } from "@/query/invalidate-member-graph";
import { groupKeys } from "@/query/keys/groups";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UUID } from "crypto";

export function useMyGroups(options?: {
  enabled?: boolean;
  staleTime?: number;
  refetchOnMount?: boolean | "always";
  refetchInterval?: number | false;
}) {
  return useQuery<GroupResponse[]>({
    queryKey: groupKeys.myGroups(),
    queryFn: () => api.groups.getMyGroups(),
    ...options,
  });
}

// All groups in the company (owner only — backed by GET /groups/company)
export function useCompanyGroups(options?: {
  enabled?: boolean;
  staleTime?: number;
}) {
  return useQuery<GroupResponse[]>({
    queryKey: groupKeys.companyGroups(),
    queryFn: () => api.groups.getCompanyGroups(),
    ...options,
  });
}

// Lean {id, name} list of the company's groups (any authenticated user —
// backed by GET /groups/options). Used by the recap_transport agence selector.
export function useGroupOptions(options?: {
  enabled?: boolean;
  staleTime?: number;
}) {
  return useQuery<GroupOption[]>({
    queryKey: groupKeys.options(),
    queryFn: () => api.groups.getGroupOptions(),
    ...options,
  });
}

export function useGroup(groupId: string) {
  return useQuery<GroupResponse>({
    queryKey: groupKeys.detail(groupId as UUID),
    queryFn: () => api.groups.getGroupId(groupId as UUID),
    enabled: !!groupId,
  });
}

export function useCreateGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: GroupCreate) => api.groups.createGroup(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: groupKeys.all });
    },
  });
}

// Rename a group/agency (owner-only server-side — backed by PUT /groups/{id}).
export function useRenameGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, name }: { groupId: UUID; name: string }) =>
      api.groups.putGroupId(groupId, { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: groupKeys.all });
    },
  });
}

export function useDeleteGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (groupId: UUID) => api.groups.deleteGroupId(groupId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: groupKeys.all });
    },
  });
}

export function useAddGroupMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, userId }: { groupId: UUID; userId: UUID }) =>
      api.groups.groupId.postAddMember(groupId, { userId }),
    onSuccess: () => {
      invalidateMemberGraph(queryClient);
    },
  });
}

export function useUpdateGroupMemberRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      groupId,
      userId,
      role,
    }: {
      groupId: UUID;
      userId: UUID;
      role: GroupMemberRoleValue;
    }) => api.groups.putMemberRole(groupId, userId, { role }),
    onMutate: async ({ groupId, userId, role }) => {
      await queryClient.cancelQueries({
        queryKey: groupKeys.detail(groupId),
      });

      const previousGroup = queryClient.getQueryData<GroupResponse>(
        groupKeys.detail(groupId),
      );

      // Optimistically update the member's role in the cached group detail
      queryClient.setQueryData<GroupResponse>(
        groupKeys.detail(groupId),
        (old) => {
          if (!old) return old;
          return {
            ...old,
            members: old.members.map((m) =>
              m.user_id === userId ? { ...m, role } : m,
            ),
          };
        },
      );

      return { previousGroup };
    },
    onError: (_err, variables, context) => {
      if (context?.previousGroup) {
        queryClient.setQueryData(
          groupKeys.detail(variables.groupId),
          context.previousGroup,
        );
      }
    },
    onSettled: () => {
      invalidateMemberGraph(queryClient);
    },
  });
}

// Transfer group ownership to another member (group owner or company owner).
export function useSetGroupOwner() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, userId }: { groupId: UUID; userId: UUID }) =>
      api.groups.putOwner(groupId, { user_id: userId }),
    onSuccess: () => {
      invalidateMemberGraph(queryClient);
    },
  });
}

// Create a new account directly inside a group (Agency manager / owner).
// The server emails a set-password link. 409 surfaces as the duplicate error.
export function useCreateGroupUser() {
  const queryClient = useQueryClient();
  return useMutation<
    GroupUserCreateResponse,
    Error,
    { groupId: UUID; data: GroupUserCreate }
  >({
    mutationFn: ({ groupId, data }) => api.groups.createUser(groupId, data),
    onSuccess: () => {
      // A group user is also a company member + employee — the whole graph.
      invalidateMemberGraph(queryClient);
    },
  });
}

// Detach a user from the group. This only removes group membership — the
// account stays active (removal is an authorization-scope change, not a
// credential/lifecycle event); backend emits `user-scope.changed` for eviction.
export function useRemoveGroupUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, userId }: { groupId: UUID; userId: UUID }) =>
      api.groups.removeUser(groupId, userId),
    onMutate: async ({ groupId, userId }) => {
      await queryClient.cancelQueries({
        queryKey: groupKeys.detail(groupId),
      });

      const previousGroup = queryClient.getQueryData<GroupResponse>(
        groupKeys.detail(groupId),
      );

      // Optimistically remove the member from the cached group detail
      queryClient.setQueryData<GroupResponse>(
        groupKeys.detail(groupId),
        (old) => {
          if (!old) return old;
          return {
            ...old,
            members: old.members.filter((m) => m.user_id !== userId),
          };
        },
      );

      return { previousGroup };
    },
    onError: (_err, variables, context) => {
      if (context?.previousGroup) {
        queryClient.setQueryData(
          groupKeys.detail(variables.groupId),
          context.previousGroup,
        );
      }
    },
    onSettled: () => {
      invalidateMemberGraph(queryClient);
    },
  });
}

// Email a password-reset link to a group member (link only).
export function useSendGroupUserRecovery() {
  return useMutation({
    mutationFn: ({ groupId, userId }: { groupId: UUID; userId: UUID }) =>
      api.groups.sendUserRecovery(groupId, userId),
  });
}

// Edit a group member's identity (name / email / phone / language). Scoped to
// the acting HR admin's group; 409 on duplicate email/phone.
export function useUpdateGroupUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      groupId,
      userId,
      data,
    }: {
      groupId: UUID;
      userId: UUID;
      data: CompanyUserUpdate;
    }) => api.groups.updateUser(groupId, userId, data),
    onSuccess: () => {
      // Name/email edits ripple through the member graph (group detail, member
      // lists, employee linkage) — invalidate the whole graph.
      invalidateMemberGraph(queryClient);
    },
  });
}

// Set a group member's password directly, or generate a temporary one (returned
// once). Distinct from send-recovery: no email, no impersonation.
export function useResetGroupUserPassword() {
  return useMutation({
    mutationFn: ({
      groupId,
      userId,
      password,
    }: {
      groupId: UUID;
      userId: UUID;
      password?: string | null;
    }) => api.groups.resetUserPassword(groupId, userId, { password }),
  });
}

export function useUpdateGroupAIContext() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      groupId,
      ai_context,
    }: {
      groupId: UUID;
      ai_context: string | null;
    }) => v2UpdateGroup(groupId, { aiContext: ai_context }),
    onSuccess: (updated, variables) => {
      queryClient.setQueryData<GroupResponse>(
        groupKeys.detail(variables.groupId),
        (prev) =>
          prev
            ? {
                ...prev,
                ...updated,
                owner_name: prev.owner_name,
                members: prev.members,
              }
            : updated,
      );
      queryClient.invalidateQueries({ queryKey: groupKeys.myGroups() });
    },
  });
}
